import type { TaskPaneReportFilter } from '../models/agreements.model';
import { buildAgreementsPayloadFromFilter } from '../utils/agreements-filter-payload.builder';
import type { AgreementsReportFetchDeps } from './agreements-fetch.deps';
import { defaultAgreementsReportFetchDeps } from './agreements-fetch.deps';

export type FetchAgreementsReportResult =
    | {
          kind: 'success';
          records: unknown[];
          recordCount: number;
          totalCount?: number;
          body: unknown;
          httpStatus: number;
      }
    | {
          kind: 'failure';
          message: string;
          source: 'validation' | 'http' | 'network';
          invalidElementIds?: readonly string[];
          httpStatus?: number;
          body?: unknown;
          debugErrorTitle: string;
      };

/**
 * Agreements feature: Get-Agreements only (no Excel). Task pane formats records and calls the tabular writer.
 */
export async function fetchAgreementsReport(
    accessToken: string,
    filter: TaskPaneReportFilter,
    deps: AgreementsReportFetchDeps = defaultAgreementsReportFetchDeps
): Promise<FetchAgreementsReportResult> {
    const payload = buildAgreementsPayloadFromFilter(filter);
    const result = await deps.fetchGetAgreements(accessToken, payload);

    if (result.kind === 'failure') {
        const source = result.source ?? 'http';
        return {
            kind: 'failure',
            message: result.message,
            source,
            ...(result.invalidElementIds !== undefined
                ? { invalidElementIds: result.invalidElementIds }
                : {}),
            ...(result.httpStatus !== undefined ? { httpStatus: result.httpStatus } : {}),
            ...(result.body !== undefined ? { body: result.body } : {}),
            debugErrorTitle: source === 'validation' ? 'Invalid filters' : 'Report fetch failed',
        };
    }

    const { body, records, totalCount, recordCount, httpStatus } = result;
    return {
        kind: 'success',
        records,
        recordCount,
        totalCount,
        body,
        httpStatus,
    };
}
